import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApplicantsPanel from './ApplicantsPanel';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
const API_BASE = process.env.REACT_APP_API_URL || `${GREENWICH_BACKEND_URL}/api`;

function OrgMySubmissions() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const STATUS_CONFIG = {
    pending:    { label: t('orgMySubmissions.statusPending'),  color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '⏳' },
    approved:   { label: t('orgMySubmissions.statusApproved'), color: '#16a34a', bg: '#dcfce7', border: '#86efac', icon: '✅' },
    rejected:   { label: t('orgMySubmissions.statusRejected'), color: '#dc2626', bg: '#fee2e2', border: '#fecaca', icon: '❌' },
    needs_edit: { label: t('orgMySubmissions.statusNeedsEdit', 'Needs Edit'), color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '✏️' },
  };

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  // Greencoin rewards are admin-only during rollout — see GREENCOIN_ORG_SELF_SERVICE on the backend.
  const isAdmin = user?.role === 'admin' || user?.userType === 'admin';

  const [programs,     setPrograms]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState('');
  const [editingId,    setEditingId]    = useState(null);
  const [editForm,     setEditForm]     = useState({});
  const [resubmitting, setResubmitting] = useState({});
  const [filter,       setFilter]       = useState('all');
  const [applicantsProgram, setApplicantsProgram] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const normalizeProgram = (p) => ({
    ...p,
    url:
      p.url              ||
      p.website          ||
      p.link             ||
      p.applicationLink  ||
      p.application_link ||
      '',
    admin_feedback:
      p.admin_feedback   ||
      p.adminFeedback    ||
      p.feedback         ||
      p.rejection_reason ||
      p.rejectionReason  ||
      p.admin_note       ||
      p.adminNote        ||
      null,
    approval_status:
      p.approval_status ||
      p.approvalStatus  ||
      p.status          ||
      'pending',
    image_url:
      p.image_url   ||
      p.imageUrl    ||
      p.coverImage  ||
      p.cover_image ||
      p.image       ||
      p.photo       ||
      '',
  });

  const isMySubmission = (p, uid, email) => {
    const byId    = p.submittedBy?._id || p.submittedBy?.id || p.submittedBy;
    const byEmail =
      p.submittedBy?.email ||
      p.organizationEmail  ||
      p.contactEmail       ||
      p.submittedByEmail   ||
      '';
    return (
      (uid   && (byId === uid || p.organizationId === uid)) ||
      (email && byEmail === email) ||
      (p.submittedByType === 'organization' && email && byEmail === email)
    );
  };

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const uid   = user._id || user.id || user.userId;
    const email = user.email;
    let list    = [];

    // 1. Dedicated endpoint
    try {
      const res = await fetch(`${API_BASE}/programs/my-submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const d   = await res.json();
        const raw = Array.isArray(d) ? d : (d.programs || d.data || []);
        if (raw.length > 0) list = raw;
      }
    } catch {}

    // 2. Org submissions endpoint
    if (list.length === 0) {
      try {
        const res = await fetch(`${API_BASE}/programs/organization-submissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d   = await res.json();
          const raw = Array.isArray(d) ? d : (d.programs || d.data || []);
          if (raw.length > 0) list = raw;
        }
      } catch {}
    }

    // 3. Filter from broader endpoints
    if (list.length === 0) {
      for (const ep of ['/programs/all', '/programs', '/programs/approved']) {
        try {
          const res = await fetch(`${API_BASE}${ep}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const d        = await res.json();
            const all      = Array.isArray(d) ? d : (d.programs || d.data || []);
            const filtered = all.filter(p => isMySubmission(p, uid, email));
            if (filtered.length > 0) { list = filtered; break; }
          }
        } catch {}
      }
    }

    // 4. localStorage fallback
    if (list.length === 0) {
      for (const key of ['org_programs', 'admin_programs', 'manual_programs']) {
        try {
          const raw = localStorage.getItem(key);
          if (!raw) continue;
          const filtered = JSON.parse(raw).filter(p => isMySubmission(p, uid, email));
          if (filtered.length > 0) { list = filtered; break; }
        } catch {}
      }
    }

    // 5. Normalize fields
    list = list.map(normalizeProgram);

    // 6. Merge localStorage feedback
    try {
      const store = JSON.parse(localStorage.getItem('org_program_feedback') || '{}');
      list = list.map(p => {
        const id = p._id || p.id;
        if (store[id] && !p.admin_feedback) return { ...p, admin_feedback: store[id].message };
        return p;
      });
    } catch {}

    setPrograms(list);
    setLoading(false);
  }, [user._id, user.id, user.userId, user.email]);

  useEffect(() => { loadPrograms(); }, [loadPrograms]);

  const handleResubmit = async (id) => {
    setResubmitting(p => ({ ...p, [id]: true }));
    const token   = localStorage.getItem('token');
    let   success = false;

    const payload = {
      ...editForm,
      url:             editForm.url || '',
      website:         editForm.url || '',
      status:          'pending',
      approval_status: 'pending',
      admin_feedback:  null,
    };

    for (const [method, ep] of [
      ['POST', `/programs/${id}/resubmit`],
      ['PUT',  `/programs/${id}`],
      ['PATCH',`/programs/${id}`],
    ]) {
      if (success) break;
      try {
        const res = await fetch(`${API_BASE}${ep}`, {
          method,
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
        });
        if (res.ok) success = true;
      } catch {}
    }

    for (const key of ['admin_programs', 'org_programs', 'manual_programs']) {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        localStorage.setItem(key, JSON.stringify(
          JSON.parse(raw).map(p => (p._id === id || p.id === id) ? { ...p, ...payload } : p)
        ));
      } catch {}
    }

    try {
      const fb = JSON.parse(localStorage.getItem('org_program_feedback') || '{}');
      delete fb[id];
      localStorage.setItem('org_program_feedback', JSON.stringify(fb));
    } catch {}

    showToast(success ? t('orgMySubmissions.resubmittedToast') : t('orgMySubmissions.savedLocallyToast'));
    setEditingId(null);
    setEditForm({});
    setResubmitting(p => ({ ...p, [id]: false }));
    loadPrograms();
  };

  const [withdrawing, setWithdrawing] = useState({});

  const handleWithdraw = async (id) => {
    if (!window.confirm(t('orgMySubmissions.confirmWithdraw', 'Withdraw this submission? This cannot be undone.'))) return;
    setWithdrawing(p => ({ ...p, [id]: true }));
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/programs/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        showToast(t('orgMySubmissions.withdrawnToast', 'Submission withdrawn'));
        setPrograms(prev => prev.filter(p => (p._id || p.id) !== id));
      } else {
        const data = await res.json().catch(() => ({}));
        showToast(data.error || t('orgMySubmissions.withdrawFailedToast', 'Failed to withdraw submission'));
      }
    } catch {
      showToast(t('orgMySubmissions.withdrawFailedToast', 'Failed to withdraw submission'));
    } finally {
      setWithdrawing(p => ({ ...p, [id]: false }));
    }
  };

  const startEdit = (prog) => {
    setEditingId(prog._id || prog.id);
    setEditForm({
      title:        prog.title        || '',
      description:  prog.description  || '',
      url:          prog.url          || prog.website || '',
      location:     prog.location     || '',
      deadline:     prog.deadline     || '',
      image_url:    prog.image_url    || '',
      requirements: prog.requirements || '',
      benefits:     prog.benefits     || '',
    });
  };

  // The "rejected" stat/filter bucket is labeled statNeedsEdit in the UI, so it needs to
  // actually include programs the backend marked 'needs_edit', not just 'rejected' — otherwise
  // an organizer whose program was sent back for edits saw no visible difference from one
  // still awaiting first review (both fell back to the generic pending pill).
  const isNeedsEditBucket = (p) => p.approval_status === 'rejected' || p.approval_status === 'needs_edit';

  const counts = {
    all:      programs.length,
    pending:  programs.filter(p => p.approval_status === 'pending').length,
    approved: programs.filter(p => p.approval_status === 'approved').length,
    rejected: programs.filter(isNeedsEditBucket).length,
  };

  const displayed = filter === 'all' ? programs : filter === 'rejected' ? programs.filter(isNeedsEditBucket) : programs.filter(p => p.approval_status === filter);

  const formatDate = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css"/>

      <div className="oms-root">
        {toast && <div className="oms-toast">{toast}</div>}

        <div className="oms-header">
          <div className="oms-header-left">
            <button className="oms-back" onClick={() => navigate('/programs')}><i className="fas fa-arrow-left"></i></button>
            <div>
              <h1 className="oms-title">{t('orgMySubmissions.title')}</h1>
              <p className="oms-sub">{t('orgMySubmissions.subtitle')}</p>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button className="oms-refresh-btn" onClick={loadPrograms} title={t('orgMySubmissions.refresh')}><i className="fas fa-sync-alt"></i></button>
            {isAdmin && (
              <button className="oms-submit-btn" style={{ background: 'linear-gradient(135deg,#ffe27a,#d69b1f)', color: '#2a1c00' }} onClick={() => navigate('/organization/greencoins')}>
                🪙 {t('orgMySubmissions.greencoinWallet')}
              </button>
            )}
            <button className="oms-submit-btn" onClick={() => navigate('/programs/submit')}>
              <i className="fas fa-plus"></i> {t('orgMySubmissions.submitNew')}
            </button>
          </div>
        </div>

        <div className="oms-stats">
          {[
            { key:'all',      label:t('orgMySubmissions.statTotal'),      color:'#64748b' },
            { key:'pending',  label:t('orgMySubmissions.statPending'),    color:'#d97706' },
            { key:'approved', label:t('orgMySubmissions.statApproved'),   color:'#16a34a' },
            { key:'rejected', label:t('orgMySubmissions.statNeedsEdit'), color:'#dc2626' },
          ].map(({ key, label, color }) => (
            <button key={key} className={`oms-stat${filter===key?' oms-stat-active':''}`} style={{ '--sc':color }} onClick={() => setFilter(key)}>
              <span className="oms-stat-num" style={{ color }}>{counts[key]}</span>
              <span className="oms-stat-label">{label}</span>
            </button>
          ))}
        </div>

        <div className="oms-content">
          {loading ? (
            <div className="oms-loading"><div className="oms-spinner"></div><p>{t('orgMySubmissions.loading')}</p></div>
          ) : displayed.length === 0 ? (
            <div className="oms-empty">
              <div className="oms-empty-icon">📭</div>
              <h3>{filter==='all' ? t('orgMySubmissions.emptyTitleAll') : t('orgMySubmissions.emptyTitleFiltered', { filter: t(`orgMySubmissions.filterNames.${filter}`) })}</h3>
              <p>{filter==='all' ? t('orgMySubmissions.emptyBodyAll') : t('orgMySubmissions.emptyBodyFiltered', { filter: t(`orgMySubmissions.filterNames.${filter}`) })}</p>
              {filter==='all' && (
                <button className="oms-cta" onClick={() => navigate('/programs/submit')}>
                  <i className="fas fa-plus"></i> {t('orgMySubmissions.submitProgram')}
                </button>
              )}
            </div>
          ) : (
            <div className="oms-list">
              {displayed.map(prog => {
                const id          = prog._id || prog.id;
                const rawStatus   = prog.approval_status;
                const statusCfg   = STATUS_CONFIG[rawStatus] || STATUS_CONFIG.pending;
                const isEditing   = editingId === id;
                const hasFeedback = !!(prog.admin_feedback);
                const needsEdit   = rawStatus === 'rejected' || rawStatus === 'needs_edit' || (rawStatus === 'pending' && hasFeedback);

                return (
                  <div key={id} className="oms-card">
                    <div className="oms-card-status-bar" style={{ background:statusCfg.bg, borderBottom:`1px solid ${statusCfg.border}` }}>
                      <span className="oms-status-pill" style={{ color:statusCfg.color }}>{statusCfg.icon} {statusCfg.label}</span>
                      {prog.createdAt && <span className="oms-submit-date">{t('orgMySubmissions.submitted', { date: formatDate(prog.createdAt) })}</span>}
                    </div>

                    <div className="oms-card-body">
                      {prog.image_url && <img src={prog.image_url} alt="" className="oms-thumb" onError={e => { e.target.style.display='none'; }}/>}

                      <div className="oms-card-content">
                        {isEditing ? (
                          <div className="oms-edit-form">
                            <h4 className="oms-edit-heading"><i className="fas fa-edit"></i> {t('orgMySubmissions.editResubmitHeading')}</h4>
                            <div className="oms-field-grid">
                              <div className="oms-field">
                                <label>{t('orgMySubmissions.fieldTitle')}</label>
                                <input value={editForm.title||''} onChange={e => setEditForm(p=>({...p,title:e.target.value}))} placeholder={t('orgMySubmissions.fieldTitlePlaceholder')}/>
                              </div>
                              <div className="oms-field">
                                <label>{t('orgMySubmissions.fieldWebsite')}</label>
                                <input value={editForm.url||''} onChange={e => setEditForm(p=>({...p,url:e.target.value}))} placeholder={t('orgMySubmissions.fieldUrlPlaceholder')}/>
                              </div>
                              <div className="oms-field">
                                <label>{t('orgMySubmissions.fieldLocation')}</label>
                                <input value={editForm.location||''} onChange={e => setEditForm(p=>({...p,location:e.target.value}))} placeholder={t('orgMySubmissions.fieldLocationPlaceholder')}/>
                              </div>
                              <div className="oms-field">
                                <label>{t('orgMySubmissions.fieldDeadline')}</label>
                                <input type="date" value={editForm.deadline||''} onChange={e => setEditForm(p=>({...p,deadline:e.target.value}))}/>
                              </div>
                              <div className="oms-field oms-field-full">
                                <label>{t('orgMySubmissions.fieldDescription')}</label>
                                <textarea rows={4} value={editForm.description||''} onChange={e => setEditForm(p=>({...p,description:e.target.value}))} placeholder={t('orgMySubmissions.fieldDescriptionPlaceholder')}/>
                              </div>
                              <div className="oms-field oms-field-full">
                                <label>{t('orgMySubmissions.fieldRequirements')}</label>
                                <textarea rows={3} value={editForm.requirements||''} onChange={e => setEditForm(p=>({...p,requirements:e.target.value}))} placeholder={t('orgMySubmissions.fieldRequirementsPlaceholder')}/>
                              </div>
                              <div className="oms-field oms-field-full">
                                <label>{t('orgMySubmissions.fieldBenefits')}</label>
                                <textarea rows={3} value={editForm.benefits||''} onChange={e => setEditForm(p=>({...p,benefits:e.target.value}))} placeholder={t('orgMySubmissions.fieldBenefitsPlaceholder')}/>
                              </div>
                              <div className="oms-field oms-field-full">
                                <label>{t('orgMySubmissions.fieldCoverImage')}</label>
                                <input value={editForm.image_url||''} onChange={e => setEditForm(p=>({...p,image_url:e.target.value}))} placeholder={t('orgMySubmissions.fieldUrlPlaceholder')}/>
                              </div>
                            </div>
                            <div className="oms-edit-actions">
                              <button className="oms-btn-cancel" onClick={() => { setEditingId(null); setEditForm({}); }}>{t('orgMySubmissions.cancel')}</button>
                              <button className="oms-btn-resubmit" disabled={!!resubmitting[id]||!editForm.title?.trim()} onClick={() => handleResubmit(id)}>
                                {resubmitting[id] ? <span className="oms-spin"/> : <><i className="fas fa-paper-plane"></i> {t('orgMySubmissions.resubmitForReview')}</>}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3 className="oms-prog-title">{prog.title}</h3>
                            {prog.description && <p className="oms-prog-desc">{prog.description.substring(0,200)}{prog.description.length>200?'…':''}</p>}
                            <div className="oms-prog-meta">
                              {prog.location && <span><i className="fas fa-map-marker-alt"></i> {prog.location}</span>}
                              {prog.deadline && <span><i className="fas fa-calendar"></i> {formatDate(prog.deadline)}</span>}
                              {prog.url && <a href={prog.url} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}><i className="fas fa-link"></i> {t('orgMySubmissions.viewProgram')}</a>}
                            </div>

                            {hasFeedback && (
                              <div className="oms-feedback-banner">
                                <div className="oms-feedback-banner-header">
                                  <i className="fas fa-envelope-open-text"></i>
                                  <strong>{t('orgMySubmissions.messageFromAdmin')}</strong>
                                </div>
                                <p>{prog.admin_feedback}</p>
                                {needsEdit && <div className="oms-feedback-cta">{t('orgMySubmissions.feedbackCta')}</div>}
                              </div>
                            )}

                            {rawStatus==='pending' && !hasFeedback && (
                              <div className="oms-pending-notice">
                                <i className="fas fa-clock"></i>
                                {t('orgMySubmissions.pendingNotice')}
                              </div>
                            )}

                            {rawStatus==='approved' && (
                              <div className="oms-approved-notice">
                                <i className="fas fa-check-circle"></i>
                                {t('orgMySubmissions.approvedNotice')}
                                <a href="/programs" target="_blank" rel="noreferrer" style={{ color:'#16a34a', fontWeight:700, marginLeft:6 }}>{t('orgMySubmissions.viewArrow')}</a>
                              </div>
                            )}

                            <div className="oms-card-actions">
                              {needsEdit && (
                                <button className="oms-btn-edit" onClick={() => startEdit(prog)}>
                                  <i className="fas fa-edit"></i>
                                  {(rawStatus==='rejected' || rawStatus==='needs_edit') ? t('orgMySubmissions.editAndResubmit') : t('orgMySubmissions.updateAndResubmit')}
                                </button>
                              )}
                              {rawStatus==='approved' && (
                                <button className="oms-btn-edit" onClick={() => setApplicantsProgram({ id, title: prog.title })}>
                                  🧾 {t('orgMySubmissions.applications', 'Applications')}
                                </button>
                              )}
                              {rawStatus==='pending' && (
                                <button className="oms-btn-withdraw" disabled={!!withdrawing[id]} onClick={() => handleWithdraw(id)}>
                                  {withdrawing[id] ? <span className="oms-spin"/> : <><i className="fas fa-trash-alt"></i> {t('orgMySubmissions.withdraw', 'Withdraw')}</>}
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <style>{`
          *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
          :root{--green:#32614d;--green-dark:#22432f;--green-light:#e8f2ed;--amber:#faa002;}
          .oms-root{font-family:'Plus Jakarta Sans',sans-serif;background:#f2f6f3;min-height:100vh;}
          .oms-toast{position:fixed;top:20px;right:16px;z-index:9999;background:var(--green-dark);color:white;padding:10px 18px;border-radius:12px;font-size:13px;font-weight:500;box-shadow:0 8px 24px rgba(50,97,77,0.3);animation:oms-toast-in 0.3s ease;}
          @keyframes oms-toast-in{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:none}}
          .oms-header{display:flex;align-items:center;justify-content:space-between;padding:20px 20px 16px;background:white;border-bottom:1px solid #dceae2;gap:12px;flex-wrap:wrap;}
          .oms-header-left{display:flex;align-items:center;gap:14px;}
          .oms-back,.oms-refresh-btn{width:36px;height:36px;border-radius:50%;border:1.5px solid #d4e8de;background:white;color:var(--green);cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:14px;transition:all 0.15s;}
          .oms-back:hover,.oms-refresh-btn:hover{background:var(--green-light);}
          .oms-title{font-size:20px;font-weight:800;color:var(--green-dark);}
          .oms-sub{font-size:12px;color:#9abda8;margin-top:2px;}
          .oms-submit-btn{display:flex;align-items:center;gap:7px;padding:9px 18px;background:var(--green);color:white;border:none;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;white-space:nowrap;}
          .oms-submit-btn:hover{background:var(--green-dark);}
          .oms-stats{display:flex;gap:10px;padding:16px 20px;background:white;border-bottom:1px solid #dceae2;flex-wrap:wrap;}
          .oms-stat{display:flex;flex-direction:column;align-items:center;gap:2px;padding:10px 20px;border-radius:12px;border:2px solid transparent;background:#f4f9f6;cursor:pointer;transition:all 0.15s;min-width:80px;}
          .oms-stat:hover{border-color:var(--sc);}
          .oms-stat.oms-stat-active{border-color:var(--sc);background:color-mix(in srgb,var(--sc) 8%,white);}
          .oms-stat-num{font-size:22px;font-weight:800;line-height:1;}
          .oms-stat-label{font-size:11px;font-weight:600;color:#9abda8;}
          .oms-content{max-width:900px;margin:0 auto;padding:20px;}
          .oms-loading{display:flex;flex-direction:column;align-items:center;gap:12px;padding:60px 20px;}
          .oms-spinner{width:36px;height:36px;border:3px solid #e2e8f0;border-top-color:var(--green);border-radius:50%;animation:oms-spin 0.8s linear infinite;}
          @keyframes oms-spin{to{transform:rotate(360deg)}}
          .oms-empty{text-align:center;padding:60px 20px;background:white;border-radius:16px;}
          .oms-empty-icon{font-size:48px;margin-bottom:16px;}
          .oms-empty h3{font-size:18px;font-weight:800;color:var(--green-dark);margin-bottom:7px;}
          .oms-empty p{color:#9abda8;margin-bottom:20px;font-size:13px;}
          .oms-cta{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;background:var(--green);color:white;border:none;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;}
          .oms-list{display:flex;flex-direction:column;gap:14px;}
          .oms-card{background:white;border-radius:14px;box-shadow:0 2px 10px rgba(50,97,77,0.08);overflow:hidden;}
          .oms-card-status-bar{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;gap:8px;}
          .oms-status-pill{font-size:13px;font-weight:700;}
          .oms-submit-date{font-size:11px;color:#9abda8;}
          .oms-card-body{display:flex;gap:14px;padding:16px;}
          .oms-thumb{width:100px;height:80px;object-fit:cover;border-radius:8px;flex-shrink:0;}
          .oms-card-content{flex:1;min-width:0;}
          .oms-prog-title{font-size:15px;font-weight:800;color:var(--green-dark);margin-bottom:5px;line-height:1.3;}
          .oms-prog-desc{font-size:12px;color:#6b8f78;line-height:1.6;margin-bottom:8px;}
          .oms-prog-meta{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:10px;}
          .oms-prog-meta span,.oms-prog-meta a{display:inline-flex;align-items:center;gap:5px;font-size:11px;color:#9abda8;text-decoration:none;}
          .oms-prog-meta a{color:var(--green);font-weight:600;}
          .oms-feedback-banner{background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:12px 14px;margin-bottom:10px;}
          .oms-feedback-banner-header{display:flex;align-items:center;gap:7px;font-size:12px;color:#92400e;margin-bottom:6px;}
          .oms-feedback-banner p{font-size:13px;color:#78350f;line-height:1.6;white-space:pre-wrap;}
          .oms-feedback-cta{margin-top:8px;padding:8px 10px;background:#fef3c7;border-radius:7px;font-size:11px;color:#92400e;font-weight:600;}
          .oms-pending-notice{display:flex;align-items:center;gap:7px;padding:9px 12px;background:#f4f9f6;border-radius:8px;font-size:12px;color:#6b8f78;margin-bottom:8px;}
          .oms-approved-notice{display:flex;align-items:center;gap:7px;padding:9px 12px;background:#dcfce7;border-radius:8px;font-size:12px;color:#16a34a;font-weight:600;margin-bottom:8px;}
          .oms-card-actions{display:flex;gap:8px;}
          .oms-btn-edit{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:1.5px solid #32614d;background:white;color:var(--green);font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;font-family:inherit;}
          .oms-btn-edit:hover{background:var(--green-light);}
          .oms-btn-withdraw{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;border:1.5px solid #dc2626;background:white;color:#dc2626;font-size:12px;font-weight:700;cursor:pointer;transition:all 0.15s;font-family:inherit;}
          .oms-btn-withdraw:hover:not(:disabled){background:#fee2e2;}
          .oms-btn-withdraw:disabled{opacity:0.5;cursor:not-allowed;}
          .oms-edit-form{background:#f4f9f6;border-radius:10px;padding:16px;}
          .oms-edit-heading{font-size:13px;font-weight:800;color:var(--green-dark);margin-bottom:14px;display:flex;align-items:center;gap:7px;}
          .oms-field-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
          .oms-field-full{grid-column:1/-1;}
          .oms-field label{display:block;font-size:11px;font-weight:700;color:#9abda8;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:5px;}
          .oms-field input,.oms-field textarea{width:100%;padding:8px 10px;border:1.5px solid #d4e8de;border-radius:7px;font-size:13px;font-family:inherit;outline:none;color:#2d4a38;background:white;resize:vertical;}
          .oms-field input:focus,.oms-field textarea:focus{border-color:var(--green);}
          .oms-edit-actions{display:flex;gap:10px;justify-content:flex-end;}
          .oms-btn-cancel{padding:8px 16px;background:transparent;border:1.5px solid #d4e8de;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;color:#6b8f78;font-family:inherit;}
          .oms-btn-resubmit{display:inline-flex;align-items:center;gap:7px;padding:9px 18px;background:var(--green);color:white;border:none;border-radius:8px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;}
          .oms-btn-resubmit:hover:not(:disabled){background:var(--green-dark);}
          .oms-btn-resubmit:disabled{opacity:0.5;cursor:not-allowed;}
          .oms-spin{display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,0.3);border-top-color:white;border-radius:50%;animation:oms-spin 0.7s linear infinite;}
          @media(max-width:640px){
            .oms-stats{gap:6px;padding:12px;} .oms-stat{padding:8px 12px;min-width:60px;} .oms-stat-num{font-size:18px;}
            .oms-card-body{flex-direction:column;} .oms-thumb{width:100%;height:160px;} .oms-field-grid{grid-template-columns:1fr;}
            .oms-header{padding:14px;} .oms-content{padding:12px;}
          }
        `}</style>

        {applicantsProgram && (
          <ApplicantsPanel
            programId={applicantsProgram.id}
            programTitle={applicantsProgram.title}
            onClose={() => setApplicantsProgram(null)}
          />
        )}
      </div>
    </>
  );
}

export default OrgMySubmissions;